/**
 * App-wide "unsaved changes" guards. A settings page with a dirty draft
 * registers one; navigation (sidebar, Settings menu, merged-page tabs) asks
 * every guard before leaving. A guard returns false (or a Promise of false) to
 * stay. With no guards registered navigation is synchronous as before.
 *
 * Workspace tabs: a guard belongs to the tab that was active when it was
 * registered (a page only mounts, and only gets dirty, while its tab is in
 * front). Navigation asks the active tab's guards; switching tabs asks none
 * (the hidden tab keeps its edits); closing a tab asks that tab's guards.
 */
const guards = new Map(); // guard fn -> owning workspace tab id (null outside tabs)
let activeGuardTabId = null;

/** Called by the workspace tabs when the active tab changes. */
export function setActiveLeaveGuardTab(tabId) {
  activeGuardTabId = tabId ?? null;
}

export function registerLeaveGuard(fn) {
  guards.set(fn, activeGuardTabId);
  return () => guards.delete(fn);
}

function guardsForTab(tabId) {
  const list = [];
  guards.forEach((owner, fn) => {
    if (owner === tabId || owner === null) list.push(fn);
  });
  return list;
}

export function hasLeaveGuards(tabId = activeGuardTabId) {
  return guardsForTab(tabId).length > 0;
}

/** Resolves true when it is safe to leave the given tab's page (default: the active tab). */
export async function confirmLeave(tabId = activeGuardTabId) {
  for (const guard of guardsForTab(tabId)) {
    try {
      if ((await guard()) === false) return false;
    } catch (_) { /* a broken guard must not trap the user */ }
  }
  return true;
}

/** Run `go` once every guard agrees (synchronously when there are none). */
export function guardedNavigate(go) {
  if (!hasLeaveGuards()) {
    go();
    return;
  }
  confirmLeave().then((ok) => { if (ok) go(); });
}
